import { useState } from 'react';
import { ChevronDownIcon, FilterIcon, XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface TableFilterSectionProps {
  activeFilterCount: number;
  onClearAll?: () => void;
  children: React.ReactNode;
}

export function TableFilterSection({ activeFilterCount, onClearAll, children }: TableFilterSectionProps) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between px-4 py-2.5">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="-ml-2 gap-2 text-sm font-medium">
            <FilterIcon className="h-4 w-4 text-muted-foreground" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="h-5 min-w-5 rounded-full px-1.5 text-[10px] font-semibold">
                {activeFilterCount}
              </Badge>
            )}
            <ChevronDownIcon className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
          </Button>
        </CollapsibleTrigger>
        {activeFilterCount > 0 && onClearAll && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            onClick={onClearAll}
          >
            <XIcon className="h-3 w-3" />
            Clear all
          </Button>
        )}
      </div>
      <CollapsibleContent>
        <div className="border-t px-4 py-3">
          <div className="grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:items-end">
            {children}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
